// One bulk enquiry batch (/bulk-enquiry/:batchId, deep-linkable). Shows what
// GET /:batchId and GET /:batchId/rows answer; the writes are the backend's
// own actions (Process, retry failed rows, apply new settings, cancel, remove
// a row), each followed by a re-read, and the files of the rows
// (FileCompletionGrid). Process runs in useProcessRun and shows in
// ProcessModal; the page follows a batch processed elsewhere by polling it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { debounce } from 'lodash'
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  PlayCircle,
  RefreshCcw01,
  RefreshCw01,
  SearchLg,
  Trash01,
  XCircle,
} from '@untitled-ui/icons-react'
import {
  MyButton,
  MyConfirmModal,
  MyDetailView,
  MyLoadingData,
  MyMetricCard,
  MyTextField,
  myToaster,
} from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { Access, formatDateTime } from '@src/services/Helper'
import { CONSENT_SETTINGS, FILE_KIND_LABEL, consentSettingLabel } from '@src/services/consentSettings'
import Service from './service'
import usePagedList from './usePagedList'
import useProcessRun from './useProcessRun'
import { isLivePhase, isTerminalPhase } from './processEvents'
import { actionError } from './errors'
import StatusChip from './components/StatusChip'
import Notice from './components/Notice'
import LoadError from './components/LoadError'
import BatchRowsTable from './components/BatchRowsTable'
import FileCompletionGrid from './components/FileCompletionGrid'
import ConsentChanges, { ektpChanged } from './components/ConsentChanges'
import FundingChips from './components/FundingChips'
import ProcessModal from './components/ProcessModal'
import {
  BATCH_STATUS,
  BLOCKING_REASON_LABEL,
  BULK_PERMISSION,
  CREATE_PERMISSION,
  NIK_SEARCH_DIGITS,
  READY_BREAKDOWN_LABEL,
  ROWS_HIDDEN_TEXT,
  ROW_STATUS,
  SEARCH_MAX_LENGTH,
  batchRowTotal,
  breakdownText,
  chipOf,
  consentSettingsChanged,
  directToClikLabel,
  fundingChips,
  plural,
  removedRowText,
  requiredFileLabels,
  retrySummaryText,
  rowSearchTerm,
} from './constants'

const ROWS_PAGE_SIZE = 50
// A batch id the API cannot serve: malformed (422 from the param validator,
// 400) or missing / out of the user's company scope (404). Retrying won't help.
const NOT_FOUND_STATUSES = [400, 404, 422]

// The confirm modal of each write.
const CONFIRMS = {
  resync: {
    title: 'Apply the current settings?',
    positive: 'Apply settings',
    color: 'primary',
    iconBg: 'bg-warning/100',
    icon: <RefreshCw01 className="text-warning/600" />,
  },
  cancel: {
    title: 'Cancel this batch?',
    positive: 'Cancel batch',
    color: 'error',
    iconBg: 'bg-error/100',
    icon: <XCircle className="text-error/600" />,
  },
  remove: {
    title: 'Remove this row?',
    positive: 'Remove row',
    color: 'error',
    iconBg: 'bg-error/100',
    icon: <Trash01 className="text-error/600" />,
  },
  leave: {
    title: 'Leave this batch?',
    positive: 'Leave',
    color: 'error',
    iconBg: 'bg-warning/100',
    icon: <AlertTriangle className="text-warning/600" />,
  },
}

// Batch states in which a row's files may change (enquiry_import.validation
// STAGE_BATCH_STATUSES): open, and processing (a stage and Process serialise
// on the row).
const FILE_STAGE_STATUSES = ['open', 'processing']

// The success toast of each action: the backend's answer, or a summary of it.
const SUCCESS_TEXT = {
  retry: (res) => retrySummaryText(res?.data),
  remove: (res) => removedRowText(res?.data),
}

// A re-read after a write failed: the page (and any file the grid holds) stays.
const STALE_TEXT = 'The batch could not be read again: its numbers may be out of date. Reload the page to see them.'

// The banner of a run hidden from view: going on, or ended.
function RunBanner({ run, onShow, onDismiss }) {
  const { phase } = run
  if (isLivePhase(phase)) {
    let text = 'Starting…'
    if (phase === 'streaming') text = `${run.current} of ${plural(run.total, 'row')} processed.`
    if (phase === 'watching') text = 'Following the run on the server; this page checks it every few seconds.'
    return (
      <Notice tone="info" title="Processing…">
        <p>{text}</p>
        <div>
          <MyButton type="button" color="secondary" variant="outlined" size="sm" customClassname="gap-2" onClick={onShow}>
            <Eye className="size-4" stroke="currentColor" /> Show progress
          </MyButton>
        </div>
      </Notice>
    )
  }
  const counts = run.summary || run.counts
  const titles = {
    done: 'Processing finished',
    stopped: 'Processing stopped early',
    failed: 'Processing stopped unexpectedly',
    refused: 'Processing did not start',
    settled: run.settled?.stale ? 'Processing stopped before it finished' : 'Processing finished',
  }
  const tone = { done: 'success', settled: run.settled?.stale ? 'warning' : 'success', stopped: 'warning' }[phase] || 'error'
  return (
    <Notice tone={tone} title={titles[phase]} onDismiss={onDismiss}>
      {(phase === 'done' || phase === 'stopped' || phase === 'failed') && (
        <p>
          {counts.created || 0} created, {counts.failed || 0} failed, {counts.skipped || 0} skipped.
        </p>
      )}
      <div>
        <MyButton type="button" color="secondary" variant="outlined" size="sm" onClick={onShow}>
          Show result
        </MyButton>
      </div>
    </Notice>
  )
}

function BatchDetail() {
  const { batchId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = useApp()
  // The rows carry e-KTP data: the API serves them only with create_enquiry too.
  const canSeeRows = hasPermission(Access.ENQUIRY, CREATE_PERMISSION)
  // Process and Retry failed rows create enquiries: bulk AND create_enquiry
  // (the backend intersects both grants per company: 403 no_create_permission).
  const canProcess = canSeeRows && hasPermission(Access.ENQUIRY, BULK_PERMISSION)

  const [batch, setBatch] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [uploadWarnings, setUploadWarnings] = useState(() => location.state?.uploadWarnings || [])
  const [confirm, setConfirm] = useState(null) // { kind: 'resync' | 'cancel' | 'leave', to? } | { kind: 'remove', row }
  const [busy, setBusy] = useState(false)
  // The file grid holds files not saved yet, uploading, or waiting for HR's
  // choice (leaving asks first)...
  const [filesDirty, setFilesDirty] = useState(false)
  // ...and, for Process, how many are not saved and whether any is on its way.
  const [filesPending, setFilesPending] = useState({ unsaved: 0, uploading: false })
  // "Check again" of the status notice (a billing check that failed).
  const [rechecking, setRechecking] = useState(false)
  // Bumped after the page's own writes, so the file grid reads its rows again.
  const [filesRefresh, setFilesRefresh] = useState(0)
  const [rowStatus, setRowStatus] = useState(null)
  const [rowSearch, setRowSearch] = useState('')
  // The search was a NIK: only its last digits were sent (rowSearchTerm).
  const [rowSearchShortened, setRowSearchShortened] = useState(false)
  const [rowPage, setRowPage] = useState(1)

  // Upload warnings arrive once, through navigation state; drop them from the
  // history entry so a reload does not show them again.
  useEffect(() => {
    if (location.state?.uploadWarnings) navigate(location.pathname, { replace: true, state: null })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Reads the batch; resolves it (null when the read failed). quiet: a failed
  // read keeps the page as it is (polling, the re-reads after a write, the
  // re-read before Process) instead of the error screen, so the files the
  // grid holds are not lost to one failed request; a batch that is gone (404)
  // still gets its screen, there is nothing left to work on. announce: a
  // quiet failure says the numbers may be out of date. Only the latest read lands.
  const loadSeq = useRef(0)
  const loadBatch = useCallback(
    async ({ quiet = false, announce = false } = {}) => {
      loadSeq.current += 1
      const seq = loadSeq.current
      try {
        const res = await Service.show(batchId)
        if (seq === loadSeq.current) {
          setBatch(res.data)
          setLoadError(null)
        }
        return res.data
      } catch (err) {
        if (seq !== loadSeq.current) return null
        if (!quiet || NOT_FOUND_STATUSES.includes(err?.status)) setLoadError(err || { message: 'Something went wrong' })
        else if (announce) myToaster({ status: err?.status || 500, message: STALE_TEXT })
        return null
      }
    },
    [batchId]
  )

  useEffect(() => {
    setBatch(null)
    setLoadError(null)
    loadBatch()
  }, [loadBatch])

  // Rows are read once the batch itself loaded (a missing batch shows its own
  // screen instead of a second error).
  const hasBatch = batch !== null
  const fetchRows = useMemo(
    () =>
      canSeeRows && hasBatch
        ? () =>
            Service.rows(batchId, {
              page: rowPage,
              limit: ROWS_PAGE_SIZE,
              status: rowStatus || undefined,
              search: rowSearch || undefined,
            })
        : null,
    [canSeeRows, hasBatch, batchId, rowPage, rowStatus, rowSearch]
  )
  const rows = usePagedList(fetchRows)
  const reloadRows = rows.reload

  // Removing the last row of the last page leaves that page empty: step back to
  // the new last page.
  const lastRowPage = rows.paginator ? Math.max(1, Number(rows.paginator.total_pages) || 1) : null
  useEffect(() => {
    if (!rows.loading && lastRowPage && rowPage > lastRowPage) setRowPage(lastRowPage)
  }, [rows.loading, lastRowPage, rowPage])

  const debouncedRowSearch = useMemo(
    () =>
      debounce((e) => {
        // A NIK never goes into the request URL: its last digits do.
        const { term, shortened } = rowSearchTerm(e.target.value)
        setRowSearch(term)
        setRowSearchShortened(shortened)
        setRowPage(1)
      }, 500),
    []
  )
  useEffect(() => () => debouncedRowSearch.cancel(), [debouncedRowSearch])

  const filterRows = (status) => {
    setRowStatus(status)
    setRowPage(1)
  }

  // A run ended (this page's, or one it followed): the rows and the file grid
  // changed on the backend (the run reads the batch itself).
  const onRunSettled = useCallback(() => {
    reloadRows()
    setFilesRefresh((n) => n + 1)
  }, [reloadRows])
  const run = useProcessRun({ batchId, batch, loadBatch, onSettled: onRunSettled })
  const runLive = isLivePhase(run.state.phase)

  // Every write is followed by a fresh read of both the batch and its rows: the
  // backend recomputes statuses and counts, the page never patches them. A
  // refusal that means the batch changed elsewhere reads it again too.
  const runAction = async (kind, action) => {
    setBusy(true)
    try {
      const res = await action()
      const text = SUCCESS_TEXT[kind]?.(res)
      myToaster(text ? { status: 200, message: text } : res)
      await Promise.all([loadBatch({ quiet: true, announce: true }), reloadRows()])
      setFilesRefresh((n) => n + 1)
      return true
    } catch (err) {
      const failure = actionError(kind, err)
      myToaster({ status: err?.status || 500, message: failure.message })
      if (failure.reload) {
        await Promise.all([loadBatch({ quiet: true }), reloadRows()])
        setFilesRefresh((n) => n + 1)
      }
      return false
    } finally {
      setBusy(false)
    }
  }

  // Files saved or removed in the grid: counts, can_process and the rows table
  // change on the backend.
  const onFilesChanged = useCallback(
    () => Promise.all([loadBatch({ quiet: true, announce: true }), reloadRows()]),
    [loadBatch, reloadRows]
  )

  // "Check again": a check the backend could not make (billing) may pass now.
  const recheck = async () => {
    setRechecking(true)
    try {
      await loadBatch({ quiet: true, announce: true })
    } finally {
      setRechecking(false)
    }
  }

  // Unsaved files: the page's own ways out ask first (the browser asks on
  // reload / tab close; the sidebar cannot be stopped under BrowserRouter).
  const leaveTo = (to, state) => (filesDirty ? setConfirm({ kind: 'leave', to, state }) : navigate(to, { state }))
  const goBack = () => leaveTo('/bulk-enquiry')
  // A new upload for this batch's company: the list opens its upload slider.
  const uploadNew = () => leaveTo('/bulk-enquiry', { openUpload: true, companyId: batch?.company?.id || null })

  const onConfirm = () => {
    if (confirm?.kind === 'resync') runAction('resync', () => Service.resyncConfig(batchId))
    if (confirm?.kind === 'cancel') runAction('cancel', () => Service.cancel(batchId))
    if (confirm?.kind === 'remove') runAction('remove', () => Service.removeRow(batchId, confirm.row.id))
    if (confirm?.kind === 'leave') navigate(confirm.to || '/bulk-enquiry', { state: confirm.state })
  }

  // Stable for the memoized rows table (a Process run re-renders the page per event).
  const onRemoveRow = useCallback((row) => !busy && setConfirm({ kind: 'remove', row }), [busy])

  // ── Process window actions ──────────────────────────────────────────────
  const onProcessClose = () => {
    const gone = run.state.refusal?.code === 'not_found'
    run.close()
    // The batch left the user's reach: the page says so.
    if (gone) loadBatch()
  }
  // The settings, applied from the Process window (it lists the changes and
  // warns about the e-KTP), then the confirm step again with the new batch.
  const onProcessResync = async () => {
    if (await runAction('resync', () => Service.resyncConfig(batchId))) run.open()
  }
  const onProcessLeave = (leave) => {
    run.close()
    leave()
  }

  if (loadError) {
    const notFound = NOT_FOUND_STATUSES.includes(loadError.status)
    return (
      <div className="flex h-full w-full flex-col bg-gray-50/50">
        <LoadError
          title={notFound ? 'Batch not found' : 'Could not load the batch'}
          message={
            notFound ? 'It does not exist, or it belongs to a company you do not have access to.' : loadError.message
          }
          onRetry={notFound ? undefined : loadBatch}
        >
          <MyButton color="secondary" variant="outlined" size="md" onClick={() => navigate('/bulk-enquiry')}>
            Back to batches
          </MyButton>
        </LoadError>
      </div>
    )
  }

  if (!batch) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50/50">
        <MyLoadingData />
      </div>
    )
  }

  const isOpen = batch.status === 'open'
  const isCanceled = batch.status === 'canceled'
  const isCompleted = batch.status === 'completed'
  // A processing batch whose run stopped sending heartbeats: every write takes
  // it back first (the backend releases the stale claim), Process takes it over.
  const isStale = batch.status === 'processing' && Boolean(batch.processing?.stale)
  // What the page may change now: an open (or stale) batch, and no run of
  // this page going on (the page's copy of the batch still says open then).
  const editable = (isOpen || isStale) && !runLive
  const counts = batch.counts || {}
  const missingText = breakdownText(batch.missing_breakdown, FILE_KIND_LABEL)
  const readyText = breakdownText(batch.ready_breakdown, READY_BREAKDOWN_LABEL)
  const requiredFiles = requiredFileLabels(batch.required_files)
  // The file grid: rows waiting for files, in a batch whose files may change,
  // that needs any file from HR at all.
  const takesFiles = FILE_STAGE_STATUSES.includes(batch.status)
  const needsFiles = requiredFiles.length > 0
  const waitingRows = (counts.incomplete || 0) + (counts.ready || 0)
  const directLabel = directToClikLabel(batch.direct_to_clik)
  const blockingReasons = batch.blocking_reasons || []
  const failedRows = counts.failed || 0
  const readyRows = counts.ready || 0

  // consent_changes: the settings the company changed since upload, as the
  // backend compared them when it blocked the batch (consent_config_changed).
  // Printed as sent; only the words come from here.
  const consentChanges = batch.consent_changes || []
  const settingsChanged = consentSettingsChanged(batch)
  const canResync = editable && settingsChanged && batch.resync_allowed !== false

  // Process and Retry failed rows (bulk + create_enquiry). Retry works on a
  // completed batch too (a row turning ready or incomplete opens it again).
  const showProcess = canProcess && editable
  const showRetry = canProcess && failedRows > 0 && (editable || (isCompleted && !runLive))
  const processLabel = isStale
    ? 'Continue processing'
    : readyRows > 0
      ? `Process ${plural(readyRows, 'ready row')}`
      : 'Process'

  // An upload warning the detail's own checks repeat (no_subscription) is shown
  // once, in the status notice.
  const shownUploadWarnings = uploadWarnings.filter((w) => !blockingReasons.includes(w.code))

  const confirmSpec = confirm ? CONFIRMS[confirm.kind] : null
  const confirmMessage = {
    resync: (
      <>
        <span className="block">The rows are checked again against the company&apos;s current settings:</span>
        <span className="mt-1 block font-medium text-gray-700">
          <ConsentChanges changes={consentChanges} />
        </span>
        {ektpChanged(consentChanges) && (
          <span className="mt-1 block">
            If HR no longer supplies the e-KTP, the e-KTP data and photos already in this batch are removed.
          </span>
        )}
        <span className="mt-1 block">This cannot be undone.</span>
      </>
    ),
    cancel:
      'Every row that is not an enquiry yet is removed with its files. Enquiries already created are not affected.',
    remove:
      `Row ${confirm?.row?.row_no ?? ''} and its files are removed from this batch. ` +
      'Rows that were duplicates of it are checked again.',
    leave:
      'Files you added are not saved yet, still uploading, or waiting for your choice. Uploads already started ' +
      'finish on the server; the rest is lost.',
  }[confirm?.kind]

  // A run of this page out of view: its progress, or its ending not looked at yet.
  const runBanner =
    !run.visible && (runLive || isTerminalPhase(run.state.phase)) ? (
      <RunBanner run={run.state} onShow={run.show} onDismiss={run.close} />
    ) : null

  // What the batch's status notice says. A completed or canceled batch needs
  // none: its status chip says it all. A stale batch is checked like an open
  // one (Process may take it over).
  let staleNotice = null
  let statusNotice = null
  if (batch.status === 'processing' && !isStale && !runLive) {
    statusNotice = (
      <Notice tone="info" title="This batch is being processed.">
        <p>This page follows it and updates by itself every few seconds.</p>
      </Notice>
    )
  } else if ((isOpen || isStale) && !runLive) {
    if (isStale) {
      staleNotice = (
        <Notice tone="warning" title="Processing stopped before it finished.">
          {canProcess && batch.can_process && <p>Press Continue processing to process its ready rows.</p>}
        </Notice>
      )
    }
    // What HR can do about a reason, right here: Process is disabled, so its
    // window (which offers the same) cannot be opened.
    const reuploadNeeded = settingsChanged && batch.resync_allowed === false
    const noticeActions = [
      blockingReasons.includes('billing_unavailable') && (
        <MyButton
          key="recheck"
          type="button"
          color="secondary"
          variant="outlined"
          size="sm"
          disabled={rechecking}
          onClick={recheck}
        >
          {rechecking ? 'Checking…' : 'Check again'}
        </MyButton>
      ),
      blockingReasons.includes('no_subscription') && (
        <MyButton
          key="billing"
          type="button"
          color="secondary"
          variant="outlined"
          size="sm"
          onClick={() => leaveTo('/company')}
        >
          Open billing
        </MyButton>
      ),
      reuploadNeeded && canSeeRows && (
        <MyButton key="upload" type="button" color="secondary" variant="outlined" size="sm" onClick={uploadNew}>
          Upload a new batch
        </MyButton>
      ),
    ].filter(Boolean)
    statusNotice = batch.can_process ? (
      isStale ? null : <Notice tone="success" title="All checks pass: the ready rows can be processed." />
    ) : (
      <Notice tone="warning" title="Cannot be processed yet">
        <ul className="list-disc pl-5">
          {blockingReasons.map((code) => (
            <li key={code}>{BLOCKING_REASON_LABEL[code] || code}</li>
          ))}
        </ul>
        {settingsChanged && (
          <div className="mt-2 flex flex-col gap-0.5">
            {consentChanges.length > 0 && <p className="font-medium">Changed since upload:</p>}
            <ConsentChanges changes={consentChanges} />
            {reuploadNeeded && (
              <p className="mt-1 font-medium">
                This batch was uploaded without e-KTP data, so it cannot take the new settings. Upload the file
                again with a new template.
              </p>
            )}
          </div>
        )}
        {noticeActions.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{noticeActions}</div>}
      </Notice>
    )
  }

  const funding = batch.funding || {}
  // The rows API refuses this user the rows (404) although the batch loads.
  const rowsRefused = rows.error?.status === 404

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-gray-50/50">
      <MyConfirmModal
        open={Boolean(confirm)}
        title={confirmSpec?.title}
        message={confirmMessage}
        icon={confirmSpec?.icon}
        bgColor={confirmSpec?.iconBg}
        positiveButtonColor={confirmSpec?.color}
        negativeActionWord="Back"
        positiveActionWord={confirmSpec?.positive}
        onClose={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
      <ProcessModal
        open={run.visible && run.state.phase !== 'idle'}
        run={run.state}
        batch={batch}
        busy={busy}
        files={filesPending}
        onStart={run.start}
        onHide={run.hide}
        onClose={onProcessClose}
        onAgain={run.open}
        onResync={onProcessResync}
        onOpenBilling={() => onProcessLeave(() => leaveTo('/company'))}
        onUploadNew={() => onProcessLeave(uploadNew)}
      />

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 px-8 pt-8 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={goBack}
            className="flex w-max items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="size-4" stroke="currentColor" /> Back to batches
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">{batch.batch_no}</h1>
            <StatusChip {...chipOf(BATCH_STATUS, batch.status)} dot />
          </div>
          <p className="text-sm text-gray-500">
            {batch.company?.name || '—'} · {batch.file_name || '—'} · uploaded {formatDateTime(batch.created_at)} by{' '}
            {batch.created_by?.name || '—'}
          </p>
        </div>

        {(editable || showRetry) && (
          <div className="flex flex-wrap gap-3">
            {showProcess && (
              <MyButton
                type="button"
                color="primary"
                variant="filled"
                size="md"
                customClassname="gap-2 w-max"
                disabled={busy || !batch.can_process}
                onClick={run.open}
              >
                <PlayCircle className="size-4" stroke="currentColor" /> {processLabel}
              </MyButton>
            )}
            {showRetry && (
              <MyButton
                type="button"
                color="secondary"
                variant="outlined"
                size="md"
                customClassname="gap-2 w-max"
                disabled={busy}
                onClick={() => runAction('retry', () => Service.retryFailed(batchId))}
              >
                <RefreshCcw01 className="size-4" stroke="currentColor" /> Retry {plural(failedRows, 'failed row')}
              </MyButton>
            )}
            {canResync && (
              <MyButton
                type="button"
                color="primary"
                variant="outlined"
                size="md"
                customClassname="gap-2 w-max"
                disabled={busy}
                onClick={() => setConfirm({ kind: 'resync' })}
              >
                <RefreshCw01 className="size-4" stroke="currentColor" /> Apply current settings
              </MyButton>
            )}
            {editable && (
              <MyButton
                type="button"
                color="error"
                variant="outlined"
                size="md"
                customClassname="gap-2 w-max"
                disabled={busy}
                onClick={() => setConfirm({ kind: 'cancel' })}
              >
                <XCircle className="size-4" stroke="currentColor" /> Cancel batch
              </MyButton>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 px-8 pb-8">
        {/* ── Notices ──────────────────────────────────────────────────────── */}
        {shownUploadWarnings.length > 0 && (
          <Notice tone="warning" title="Uploaded with warnings" onDismiss={() => setUploadWarnings([])}>
            <ul className="list-disc pl-5">
              {shownUploadWarnings.map((w) => (
                <li key={w.code}>{w.message}</li>
              ))}
            </ul>
          </Notice>
        )}

        {runBanner}
        {staleNotice}
        {statusNotice}

        {/* ── Batch settings (snapshot taken at upload) ─────────────────────── */}
        <div className="max-w-2xl">
          <MyDetailView
            header="Consent settings of this batch"
            datas={{
              ...Object.fromEntries(
                Object.entries(CONSENT_SETTINGS).map(([key, { label }]) => [
                  label,
                  consentSettingLabel(key, batch.consent_mode?.[key]),
                ])
              ),
              'Files HR supplies': requiredFiles.length ? requiredFiles.join(', ') : 'None',
              'Direct to CLIK (no invitation)': directLabel || 'None: every row gets an invitation',
            }}
          />
        </div>

        {/* ── Counts (click to filter the rows) ────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-4">
            {/* Select, never toggle: MyMetricCard calls onClick on Enter / Space AND
                the button's own click follows, so a toggle would undo itself.
                "Total rows" clears the filter. */}
            <MyMetricCard
              label="Total rows"
              value={batchRowTotal(batch)}
              active={canSeeRows && !rowStatus}
              onClick={canSeeRows ? () => filterRows(null) : undefined}
              className="w-full md:w-auto"
            />
            {Object.entries(ROW_STATUS).map(([key, { label }]) => (
              <MyMetricCard
                key={key}
                label={label}
                value={counts[key] ?? 0}
                active={canSeeRows && rowStatus === key}
                onClick={canSeeRows ? () => filterRows(key) : undefined}
                className="w-full md:w-auto"
              />
            ))}
          </div>
          {(missingText || readyText || isCanceled) && (
            <div className="flex flex-col gap-0.5 text-sm text-gray-600">
              {isCanceled && (
                <p>
                  Canceled: the rows that were not enquiries yet were removed with their files. Total rows is the
                  number of rows in the uploaded file.
                </p>
              )}
              {missingText && <p>Missing files: {missingText}</p>}
              {readyText && <p>Ready: {readyText}</p>}
            </div>
          )}
          {/* Funding of the created rows, live from their enquiries. */}
          {fundingChips(funding).length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span>Enquiries created:</span>
              <FundingChips funding={funding} />
            </div>
          )}
          {(funding.pending_payment || 0) > 0 && (
            <p className="text-sm text-gray-600">
              Pending Payment enquiries are paid automatically in upload order after a top-up, a few per minute, so
              they do not all clear at once.
            </p>
          )}
        </div>

        {/* ── Files of the rows ──────────────────────────────────────────────── */}
        {/* Not while the rows are refused (404): the notice below says why. */}
        {canSeeRows && !rowsRefused && takesFiles && needsFiles && waitingRows > 0 && (
          <FileCompletionGrid
            key={batch.id}
            batch={batch}
            refreshKey={filesRefresh}
            onChanged={onFilesChanged}
            onDirtyChange={setFilesDirty}
            onPendingChange={setFilesPending}
          />
        )}
        {canSeeRows && takesFiles && !needsFiles && (
          <Notice tone="info" title="Nothing to upload">
            <p>In this company candidates upload their own e-KTP and HR supplies no document.</p>
          </Notice>
        )}

        {/* ── Rows ─────────────────────────────────────────────────────────── */}
        {canSeeRows && rowsRefused ? (
          // The batch is in reach (bulk grant) but its rows are not: they need
          // create_enquiry for this batch's company (the backend intersects both).
          <Notice tone="warning" title="Rows are hidden">
            <p>{ROWS_HIDDEN_TEXT}</p>
          </Notice>
        ) : canSeeRows ? (
          <div className="flex min-h-[320px] flex-col overflow-hidden rounded-xl border border-gray/200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-gray/200 bg-gray/25 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-[14px] font-semibold text-gray-900">Rows</h3>
                {rowStatus && <StatusChip {...chipOf(ROW_STATUS, rowStatus)} />}
              </div>
              <div className="flex w-full max-w-sm flex-col gap-1">
                <MyTextField
                  id="input-search-bulk-rows"
                  placeholder="Search name, email, Employee ID or NIK"
                  maxLength={SEARCH_MAX_LENGTH}
                  startAdornment={<SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />}
                  focusColor="#14378E"
                  onChangeForm={debouncedRowSearch}
                />
                {rowSearchShortened && (
                  <span className="text-xs text-gray-500">
                    Searching the last {NIK_SEARCH_DIGITS} digits of the NIK: a full NIK is never sent in a search.
                  </span>
                )}
              </div>
            </div>
            <BatchRowsTable
              rows={rows}
              hrKtp={batch.needs_hr_ktp !== false}
              canRemove={editable}
              onRemove={onRemoveRow}
              onChangePage={setRowPage}
            />
          </div>
        ) : (
          <Notice tone="warning" title="Rows are hidden">
            <p>Seeing the rows of a batch also needs the Create Enquiry permission.</p>
          </Notice>
        )}
      </div>
    </div>
  )
}

export default BatchDetail
